import { blogSeoMetadata } from '../../data/blogSeoMetadata';

export interface AnalyticsPageMetadata {
  pageName: string;
  pageSection: string;
  /** A route template, never a URL containing client IDs, tokens or search terms. */
  pageRoute: string;
}

type PageDefinition = readonly [route: string, name: string, section: string];

// Keep static routes before parameterized siblings (for example /recipes/create).
const pages: readonly PageDefinition[] = [
  ['/', 'Website Home', 'Website'],
  ['/start', 'Get Started', 'Website'],
  ['/about', 'About CalorieScience', 'Website'],
  ['/companion-app', 'Companion App', 'Website'],
  ['/llm-info', 'CalorieScience Product Information', 'Website'],
  ['/platform/biomarker-informed-nutrition', 'Biomarker Informed Nutrition', 'Website'],
  ['/personalised-nutrition-platform', 'Personalised Nutrition Platform', 'Website'],
  ['/nutrition-software-comparison', 'Nutrition Software Comparison', 'Website'],
  ['/terms', 'Website Terms of Use', 'Legal'],
  ['/platform-terms', 'Platform Terms of Use', 'Legal'],
  ['/privacy', 'Website Privacy Policy', 'Legal'],
  ['/platform-privacy', 'Platform Privacy Policy', 'Legal'],
  ['/cookies', 'Cookie Policy', 'Legal'],
  ['/refund-cancellation-policy', 'Refund & Cancellation Policy', 'Legal'],
  ['/founding-circle-agreement', 'Founding Circle Agreement', 'Legal'],
  ['/data-processing-agreement', 'Data Processing Agreement', 'Legal'],
  ['/practitioner-declaration-consent', 'Practitioner Declaration and Consent', 'Legal'],
  ['/blog/:slug', 'Blog Article', 'Blog'],
  ['/signup', 'Sign Up', 'Authentication'],
  ['/signup/step-1', 'Complete Sign Up', 'Authentication'],
  ['/login', 'Log In', 'Authentication'],
  ['/forgot-password', 'Reset Password', 'Authentication'],
  ['/auth/consent-required', 'Account Consent', 'Authentication'],
  ['/auth/subscription-required', 'Subscription Required', 'Authentication'],
  ['/auth/callback', 'Authentication Callback', 'Authentication'],
  ['/auth/onboarding-welcome', 'Onboarding Welcome', 'Onboarding'],
  ['/onboarding/practice-type', 'Choose Practice Type', 'Onboarding'],
  ['/onboarding/practice-type/individual', 'Individual Practice Setup', 'Onboarding'],
  ['/onboarding/practice-type/institution', 'Institution Practice Setup', 'Onboarding'],
  ['/client-consent', 'Client Consent', 'Client Portal'],
  ['/dashboard', 'Dashboard', 'Dashboard'],
  ['/dashboard/home', 'Dashboard', 'Dashboard'],
  ['/notifications', 'Notifications', 'Dashboard'],
  ['/appointments', 'Appointments', 'Appointments'],
  ['/appointments/calendar', 'Appointment Calendar', 'Appointments'],
  ['/clients', 'Clients', 'Clients'],
  ['/clients/add', 'Add Client', 'Clients'],
  ['/clients/:id/nutritional-targets', 'Client Nutritional Targets', 'Clients'],
  ['/clients/:id', 'Client Overview', 'Clients'],
  ['/meal-planning', 'Meal Planning', 'Meal Planning'],
  ['/generic-meal-plans', 'Cohort Meal Plan Library', 'Meal Planning'],
  ['/generic-meal-plans/new', 'Create Cohort Meal Plan', 'Meal Planning'],
  ['/meal-planning/generic/:planId/nutrition/:dayNumber', 'Cohort Meal Plan Nutrition', 'Meal Planning'],
  ['/meal-planning/client/:id/meal-plan', 'Client Meal Plans', 'Meal Planning'],
  ['/ai-meal-planning', 'Create AI Meal Plan', 'Meal Planning'],
  ['/manual-meal-plan', 'Create Manual Meal Plan', 'Meal Planning'],
  ['/auto-meal-planning', 'Create Automated Meal Plan', 'Meal Planning'],
  ['/manual-meal-plan/:draftId/client/:clientId', 'Manual Meal Plan Draft', 'Meal Planning'],
  ['/ai-meal-plan/draft/:draftId/client/:clientId', 'AI Meal Plan Draft', 'Meal Planning'],
  ['/ai-meal-plan/draft/:draftId/client/:clientId/recipe/:recipeId', 'AI Meal Plan Recipe Details', 'Meal Planning'],
  ['/automated-meal-plan/draft/:draftId/client/:clientId', 'Automated Meal Plan Draft', 'Meal Planning'],
  ['/finalized-meal-plan/:planId/client/:clientId', 'Finalized Meal Plan', 'Meal Planning'],
  ['/meal-plans/generic/:planId/nutrition/:dayNumber', 'Cohort Meal Plan Nutrition Analysis', 'Meal Planning'],
  ['/meal-plans/:planId/clients/:clientId/nutrition/:dayNumber', 'Meal Plan Nutrition Analysis', 'Meal Planning'],
  ['/recipes', 'Recipe Library', 'Recipes'],
  ['/recipes/create', 'Create Custom Recipe', 'Recipes'],
  ['/recipes/:id/edit', 'Edit Custom Recipe', 'Recipes'],
  ['/recipes/:id', 'Recipe Details', 'Recipes'],
  ['/ingredients', 'Ingredient Library', 'Recipes'],
  ['/templates', 'Meal Plan Templates', 'Templates'],
  ['/templates/:templateId/view', 'Meal Plan Template Preview', 'Templates'],
  ['/questionnaires', 'Core Questionnaires', 'Questionnaires'],
  ['/questionnaires/:questionnaireId', 'Questionnaire Details', 'Questionnaires'],
  ['/questionnaires/:questionnaireId/assign', 'Assign Questionnaire', 'Questionnaires'],
  ['/questionnaires/:questionnaireId/responses', 'Questionnaire Responses', 'Questionnaires'],
  ['/questionnaires/:questionnaireId/responses/:instanceId', 'Questionnaire Response Details', 'Questionnaires'],
  ['/questionnaire/share/:shareId', 'Complete Client Questionnaire', 'Client Portal'],
  ['/book/:linkId', 'Book Appointment', 'Client Portal'],
  ['/reschedule/:linkId', 'Reschedule Appointment', 'Client Portal'],
  ['/invoicing', 'All Invoices', 'Invoicing'],
  ['/knowledgebase', 'Knowledge Base', 'Support'],
  ['/profile', 'Settings: Profile Details', 'Settings'],
  ['/feedback', 'Product Feedback', 'Support'],
  ['/admin/users', 'Admin: User Management', 'Administration'],
  ['/admin/usage-quality', 'Admin: Usage and Quality', 'Administration'],
  ['/admin/landing-operations', 'Admin: Landing Operations', 'Administration'],
  ['/admin/client-invites', 'Admin: Invite Client', 'Administration'],
  ['/admin/custom-recipes', 'Admin: Custom Recipe Review', 'Administration'],
  ['/admin/ai-recipes', 'Admin: AI Recipe Review', 'Administration'],
  ['/admin/feedback', 'Admin: Practitioner Feedback', 'Administration'],
];

const compiledPages = pages.map(([pageRoute, pageName, pageSection]) => ({
  metadata: { pageName, pageSection, pageRoute },
  pattern: new RegExp(`^${pageRoute.replace(/:[^/]+/g, '[^/]+')}$`, 'i'),
}));

const clientSections = new Map([
  ['overview', 'Client Overview'],
  ['appointments', 'Client Appointments'],
  ['suggested-rda', 'Client Suggested RDA'],
  ['client-targets', 'Client Nutritional Targets'],
  ['analysis', 'Client Nutrition Analysis'],
  ['meal-plan', 'Client Meal Plans'],
  ['lab-reports', 'Client Lab Reports'],
  ['questionnaires', 'Client Questionnaires'],
  ['invoices', 'Client Invoices'],
  ['flags-and-notes', 'Client Notes'],
]);

const profileTabs = new Map([
  ['settings', 'Profile Details'],
  ['availability', 'Availability'],
  ['billing', 'Subscription Plan'],
  ['feedback', 'Product Feedback'],
]);

const profileViews = new Map([
  ['hub', 'Settings Home'],
  ['profile-details', 'Profile Details'],
  ['practice-info', 'Practice Information'],
  ['notifications', 'Notification Preferences'],
  ['security', 'Security'],
  ['plans-packages', 'Plans and Packages'],
  ['subscription-plan', 'Subscription Plan'],
  ['upgrade-plan', 'Upgrade Plan'],
  ['invoice-tax-details', 'Invoice and Tax Details'],
]);

const availabilitySections = new Map([
  ['weekly', 'Weekly Availability'],
  ['breaks', 'Recurring Breaks'],
  ['timeoff', 'Time Off'],
]);

const invoiceTabs = new Map([
  ['all', 'All Invoices'],
  ['paid', 'Paid Invoices'],
  ['due', 'Due Invoices'],
  ['archived', 'Archived Invoices'],
]);

const getProfilePageName = (params: URLSearchParams): string => {
  // Match MyProfilePage: a recognized tab takes precedence over a view.
  const tab = params.get('tab') ?? '';
  if (tab === 'availability') {
    return `Settings: ${availabilitySections.get(params.get('section') ?? '') ?? 'Weekly Availability'}`;
  }
  return `Settings: ${profileTabs.get(tab) ?? profileViews.get(params.get('view') ?? '') ?? 'Profile Details'}`;
};

/**
 * Human-readable, bounded page identities for reports and click context.
 * Only recognized routes and UI query values may influence the result; raw
 * identifiers, questionnaire tokens, client names and search text never do.
 */
export const getAnalyticsPageMetadata = (pathname: string, search = '', demoMode?: boolean): AnalyticsPageMetadata => {
  const pathWithoutHash = pathname.split('#', 1)[0];
  const queryIndex = pathWithoutHash.indexOf('?');
  const path = (queryIndex < 0 ? pathWithoutHash : pathWithoutHash.slice(0, queryIndex)).replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(search || (queryIndex < 0 ? '' : pathWithoutHash.slice(queryIndex)));
  const clientMatch = path.match(/^\/clients\/[^/]+\/details-v2(?:\/(.*))?$/i);
  let metadata: AnalyticsPageMetadata;

  if (clientMatch) {
    const requestedSection = clientMatch[1]?.split('/')[0].trim().toLowerCase() ?? '';
    const section = clientSections.has(requestedSection) ? requestedSection : 'overview';
    metadata = {
      pageName: clientSections.get(section)!,
      pageSection: 'Clients',
      pageRoute: `/clients/:id/details-v2/${section}`,
    };
  } else {
    metadata = { ...(compiledPages.find(({ pattern }) => pattern.test(path))?.metadata ?? {
      pageName: 'Page Not Found',
      pageSection: 'Other',
      pageRoute: '*',
    }) };
  }

  switch (metadata.pageRoute) {
    case '/profile':
      metadata.pageName = getProfilePageName(params);
      break;
    case '/questionnaires':
      metadata.pageName = params.get('tab') === 'custom' ? 'Custom Questionnaires' : 'Core Questionnaires';
      break;
    case '/templates':
      metadata.pageName = params.get('tab') === 'meal_slot' ? 'Meal Slot Templates' : 'Meal Plan Templates';
      break;
    case '/invoicing':
      metadata.pageName = invoiceTabs.get(params.get('tab')?.trim().toLowerCase() ?? '') ?? 'All Invoices';
      break;
    case '/meal-planning/client/:id/meal-plan':
      if (params.get('mealPlanId')?.trim()) metadata.pageName = 'Client Meal Plan Details';
      break;
    case '/meal-plans/:planId/clients/:clientId/nutrition/:dayNumber':
      if (params.get('template') === 'true') metadata.pageName = 'Meal Plan Template Preview';
      break;
    case '/blog/:slug': {
      const article = blogSeoMetadata.find(({ href }) => href.toLowerCase() === path.toLowerCase());
      if (article) metadata.pageName = `Blog: ${article.title}`;
      break;
    }
  }

  const demo = params.get('demo')?.toLowerCase();
  // Explicit query values override demo state inherited from session or config.
  const isDemo = demo === 'true' || demo === '1'
    ? true
    : demo === 'false' || demo === '0'
      ? false
      : demoMode === true;
  if (isDemo) metadata.pageName = `Demo: ${metadata.pageName}`;
  return metadata;
};
